export const mercatorPoint = ([lng, lat]: number[]): [number, number] => [
  (lng + 180) / 360,
  (1 - Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)) / Math.PI) / 2,
];
const geographicPoint = ([x, y]: number[]): [number, number] => [
  x * 360 - 180, Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI,
];

// Fit the actual footprint in a virtual north-up camera, then rotate that
// camera back. This avoids padding out a diagonal parcel's north-up bounds.
export function replayFrame(points: number[][], width: number, height: number, _pitch: number) {
  const projected = points.map(mercatorPoint);
  const origin = projected[0];
  let best = { bearing: 0, score: Infinity, bounds: [[0, 0], [0, 0]] };
  for (let bearing = -90; bearing < 90; bearing += 2) {
    const angle = bearing * Math.PI / 180, c = Math.cos(angle), s = Math.sin(angle);
    const rotated = projected.map(([x, y]) => [(x - origin[0]) * c + (y - origin[1]) * s, -(x - origin[0]) * s + (y - origin[1]) * c]);
    const xs = rotated.map(p => p[0]), ys = rotated.map(p => p[1]);
    const bounds = [[Math.min(...xs), Math.min(...ys)], [Math.max(...xs), Math.max(...ys)]];
    const spanX = Math.max(1e-12, bounds[1][0] - bounds[0][0]);
    const spanY = Math.max(1e-12, bounds[1][1] - bounds[0][1]);
    // Minimize the footprint rectangle, rather than turning a long site
    // diagonally merely to squeeze a little more zoom from the viewport.
    const landscape = width >= height;
    const wrongOrientation = landscape ? spanY > spanX : spanX > spanY;
    const score = spanX * spanY * (wrongOrientation ? 2 : 1);
    // Prefer north-up when rotating would give no meaningful improvement.
    const weighted = score * (1 + Math.abs(bearing) / 90 * 0.015);
    if (weighted < best.score) best = { bearing, score: weighted, bounds };
  }
  const angle = best.bearing * Math.PI / 180, c = Math.cos(angle), s = Math.sin(angle);
  return {
    bearing: best.bearing,
    bounds: best.bounds.map(([x, y]) => geographicPoint([x + origin[0], y + origin[1]])) as [[number, number], [number, number]],
    restoreCenter(center: number[]) {
      const [x, y] = mercatorPoint(center), dx = x - origin[0], dy = y - origin[1];
      return geographicPoint([origin[0] + dx * c - dy * s, origin[1] + dx * s + dy * c]);
    },
  };
}

export function routeDistances(points: number[][]) {
  const projected = points.map(mercatorPoint);
  const distances = [0];
  for (let i = 1; i < projected.length; i++) distances.push(distances[i - 1] + Math.hypot(projected[i][0] - projected[i - 1][0], projected[i][1] - projected[i - 1][1]));
  return distances;
}

export function routeProgress(distances: number[], index: number, fraction: number) {
  const total = distances[distances.length - 1];
  if (!total) return 0;
  return Math.max(0, Math.min(1, (distances[index] + (distances[index + 1] - distances[index]) * fraction) / total));
}
